export type CribnoshAppId = "nosher" | "creator" | "driver";

export type ClientPlatform = "ios" | "android" | "desktop";

export type CribnoshAppLinkConfig = {
  id: CribnoshAppId;
  scheme: string;
  iosAppStoreUrl: string;
  androidPlayStoreUrl: string;
  defaultWebHref: string;
  defaultDeepLinkPath: string;
};

export const CRIBNOSH_APP_LINKS: Record<CribnoshAppId, CribnoshAppLinkConfig> = {
  nosher: {
    id: "nosher",
    scheme: "cribnoshapp://",
    iosAppStoreUrl: "https://apps.apple.com/app/id6755092841",
    androidPlayStoreUrl:
      "https://play.google.com/store/apps/details?id=com.cribnosh.co.uk",
    defaultWebHref: "/customer",
    defaultDeepLinkPath: "/orders",
  },
  creator: {
    id: "creator",
    scheme: "cribnoshfoodcreator://",
    iosAppStoreUrl: "https://apps.apple.com/app/id6755956753",
    androidPlayStoreUrl:
      "https://play.google.com/store/apps/details?id=com.cribnosh.foodcreators",
    defaultWebHref: "/cooking",
    defaultDeepLinkPath: "/profile",
  },
  driver: {
    id: "driver",
    scheme: "cribnosh-driver://",
    iosAppStoreUrl: "https://apps.apple.com/app/id6773700457",
    androidPlayStoreUrl:
      "https://play.google.com/store/apps/details?id=com.cribnosh.driver",
    defaultWebHref: "/driving",
    defaultDeepLinkPath: "/",
  },
};

export const FOOD_CREATOR_APP_LINK_BASE_URL = "https://foodcreators.cribnosh.com";
export const FOOD_CREATOR_APP_SCHEME_BASE = CRIBNOSH_APP_LINKS.creator.scheme;
export const FOOD_CREATOR_IOS_APP_STORE_URL =
  CRIBNOSH_APP_LINKS.creator.iosAppStoreUrl;

const IOS_USER_AGENT_PATTERN =
  /iphone|ipad|ipod/i;

const ANDROID_USER_AGENT_PATTERN = /android/i;

const MAC_OS_USER_AGENT_PATTERN = /macintosh/i;

export function getCribnoshAppLinkConfig(appId: CribnoshAppId): CribnoshAppLinkConfig {
  return CRIBNOSH_APP_LINKS[appId];
}

export function normalizeCribnoshAppPath(appPath = "/"): string {
  const trimmed = appPath.trim();
  if (!trimmed) {
    return "/";
  }

  return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
}

export function buildCribnoshAppSchemeUrl(
  appId: CribnoshAppId,
  appPath = "/",
): string {
  const normalizedPath = normalizeCribnoshAppPath(appPath);
  return `${CRIBNOSH_APP_LINKS[appId].scheme}${normalizedPath.replace(/^\/+/, "")}`;
}

const hasAnyQueryParam = (url: URL, keys: string[]) =>
  keys.some((key) => {
    const value = url.searchParams.get(key);
    return typeof value === "string" && value.trim().length > 0;
  });

const isSupportedNosherAppPath = (appPath: string): boolean => {
  const normalizedPath = normalizeCribnoshAppPath(appPath);
  const parsed = new URL(normalizedPath, "https://cribnosh.com");
  const segments = parsed.pathname
    .split("/")
    .map((segment) => segment.trim())
    .filter((segment) => segment.length > 0);
  const [section, second, third] = segments;

  if (!section) return false;

  if (section === "pay") {
    return typeof second === "string" && second.length > 0;
  }

  if (section === "treat") {
    return typeof second === "string" && second.length > 0;
  }

  if (section === "group-order") {
    return typeof second === "string" && second.length > 0;
  }

  if (section === "orders") {
    if (second === "shared") {
      return typeof third === "string" && third.length > 0;
    }
    return true;
  }

  if (
    section === "profile" ||
    section === "sign-in" ||
    section === "signin" ||
    section === "login" ||
    section === "ai-chat" ||
    section === "ai" ||
    section === "assistant" ||
    section === "shared-link" ||
    section === "order-status-tracking" ||
    section === "nosh-heaven" ||
    section === "cooking" ||
    section === "by-us" ||
    section === "story" ||
    section === "stories" ||
    section === "meal-import" ||
    section === "play-to-win" ||
    section === "birthday"
  ) {
    return true;
  }

  if (
    section === "foodcreator" ||
    section === "food-creator" ||
    section === "foodcreators" ||
    section === "food-creators"
  ) {
    return typeof second === "string" && second.length > 0;
  }

  if (section === "custom-order-details") {
    return hasAnyQueryParam(parsed, ["id", "orderId", "order_id"]);
  }

  if (section === "live") {
    return (
      (typeof second === "string" && second.length > 0) ||
      hasAnyQueryParam(parsed, ["sessionId", "id"])
    );
  }

  if (section === "meal-insights") {
    if (second === "import") {
      return true;
    }
    return typeof second === "string" && second.length > 0;
  }

  return false;
};

const isSupportedCreatorAppPath = (appPath: string): boolean => {
  const normalizedPath = normalizeCribnoshAppPath(appPath);
  const parsed = new URL(normalizedPath, "https://foodcreators.cribnosh.com");
  const segments = parsed.pathname
    .split("/")
    .map((segment) => segment.trim())
    .filter((segment) => segment.length > 0);
  const [section, second, third] = segments;

  if (!section) return false;

  if (
    section === "live" ||
    section === "orders" ||
    section === "profile" ||
    section === "food-safety-spotcheck" ||
    section === "creator-map"
  ) {
    return true;
  }

  if (section === "meals") {
    return (
      typeof second === "string" &&
      second.length > 0 &&
      third === "edit"
    );
  }

  return false;
};

export function isSupportedCribnoshAppPath(
  appId: CribnoshAppId,
  appPath = "/",
): boolean {
  const normalizedPath = normalizeCribnoshAppPath(appPath);

  if (appId === "nosher") {
    return isSupportedNosherAppPath(normalizedPath);
  }

  if (appId === "creator") {
    return isSupportedCreatorAppPath(normalizedPath);
  }

  return normalizedPath === "/";
}

export function resolveCribnoshAppLaunchTarget(
  appId: CribnoshAppId,
  appPath = "/",
  webFallbackHref?: string,
) {
  const config = getCribnoshAppLinkConfig(appId);
  const requestedPath = normalizeCribnoshAppPath(appPath);
  const requestedPathIsRoot = requestedPath === "/";
  const requestedPathSupported = isSupportedCribnoshAppPath(appId, requestedPath);
  const resolvedAppPath =
    requestedPathIsRoot
      ? config.defaultDeepLinkPath
      : requestedPathSupported
        ? requestedPath
        : config.defaultDeepLinkPath;
  const canOpenApp =
    requestedPathIsRoot || requestedPathSupported;

  return {
    requestedPath,
    requestedPathSupported,
    resolvedAppPath,
    canOpenApp,
    resolvedWebFallbackHref: webFallbackHref || config.defaultWebHref,
  };
}

export function detectClientPlatform(
  userAgent: string,
  maxTouchPoints = 0,
): ClientPlatform {
  if (ANDROID_USER_AGENT_PATTERN.test(userAgent)) {
    return "android";
  }

  if (
    IOS_USER_AGENT_PATTERN.test(userAgent) ||
    (MAC_OS_USER_AGENT_PATTERN.test(userAgent) && maxTouchPoints > 1)
  ) {
    return "ios";
  }

  return "desktop";
}

export function getPlatformInstallHref(
  appId: CribnoshAppId,
  platform: ClientPlatform,
): string {
  const config = CRIBNOSH_APP_LINKS[appId];

  if (platform === "ios") {
    return config.iosAppStoreUrl;
  }

  if (platform === "android") {
    return config.androidPlayStoreUrl;
  }

  return config.defaultWebHref;
}
